import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { ConversationItem, ConversationListResponse } from '@chatapp-inbox/shared';
import { conversationKey } from './keys';

const LISTS = ['conversations'] as const;
/** Resultados de busca (não são remendados pelo tempo real: são refeitos). */
export const SEARCHES = ['conversation-search'] as const;

type CachedPages = InfiniteData<ConversationListResponse, string | null>;

/** Troca, nas listas já carregadas, a conversa de mesmo id (a posição e as demais não mudam). */
export function patchConversationInLists(queryClient: QueryClient, item: ConversationItem): void {
  queryClient.setQueriesData<CachedPages>({ queryKey: LISTS }, (cached) => {
    if (!cached) return cached;
    let touched = false;
    const pages = cached.pages.map((page) => {
      if (!page.conversations.some((c) => c.id === item.id)) return page;
      touched = true;
      return {
        ...page,
        conversations: page.conversations.map((c) => (c.id === item.id ? item : c)),
      };
    });
    return touched ? { ...cached, pages } : cached;
  });
}

/**
 * Pede de novo as listas (as em uso refazem na hora; as demais ficam obsoletas). Serve quando a
 * mudança pode alterar QUAIS conversas aparecem em cada aba ou a ORDEM: atribuir, liberar, enviar.
 */
export function invalidateConversationLists(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: LISTS }),
    queryClient.invalidateQueries({ queryKey: SEARCHES }),
  ]).then(() => undefined);
}

/**
 * Registra uma versão nova da conversa (resposta de `assign`/`read`): atualiza o cache da conversa
 * aberta e o item nas listas. Não refaz as listas; quem muda a atribuição chama
 * `invalidateConversationLists` em seguida.
 */
export function syncConversation(queryClient: QueryClient, item: ConversationItem): void {
  queryClient.setQueryData(conversationKey(item.id), item);
  patchConversationInLists(queryClient, item);
}
